// RecommendedService.js
import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";

// Import all service data from Service.js or redefine here
const servicesData = {
  "pain-clinic": {
    title: "PAIN CLINIC",
    description:
      "Pain management is a new branch of modern medicine that addresses all aspects of 'pain'. This is an emerging specialty in India, with new centres coming up fast.",
    longDescription:
      "Our pain clinic offers comprehensive evaluation and treatment for acute and chronic pain conditions. We utilize a multidisciplinary approach combining medication management, interventional procedures, physical therapy, and psychological support to help patients regain function and improve quality of life.",
    icon: "🔥",
    color: "#ff6b6b",
    treatments: [
      "Epidural Steroid Injections",
      "Nerve Blocks",
      "Radiofrequency Ablation",
      "Spinal Cord Stimulation",
      "Medication Management",
    ],
    conditions: [
      "Chronic Back Pain",
      "Neck Pain",
      "Neuropathic Pain",
      "Complex Regional Pain Syndrome",
      "Post-Surgical Pain",
    ],
  },
  "cervical-clinic": {
    title: "CERVICAL CLINIC",
    description:
      "The goal of cervical spine surgery is to relieve pain, tingling and weakness, restore nerve function and stop or prevent abnormal motion in the spine.",
    longDescription:
      "Our cervical spine clinic specializes in the diagnosis and treatment of disorders affecting the neck and upper spine. We offer both surgical and non-surgical treatment options tailored to each patient's specific condition and lifestyle needs.",
    icon: "🦴",
    color: "#4ecdc4",
    treatments: [
      "Anterior Cervical Discectomy and Fusion (ACDF)",
      "Cervical Disc Replacement",
      "Posterior Cervical Laminectomy",
      "Physical Therapy",
      "Pain Management",
    ],
    conditions: [
      "Cervical Radiculopathy",
      "Cervical Myelopathy",
      "Herniated Disc",
      "Cervical Stenosis",
      "Whiplash Injuries",
    ],
  },
  "deformity-clinic": {
    title: "DEFORMITY CLINIC",
    description:
      "Scoliosis is an abnormal curvature of the spine that can occur in any age group. Kyphosis is seen when a person's spinal balance has moved too far forward.",
    longDescription:
      "Our deformity clinic specializes in the evaluation and treatment of complex spinal deformities including scoliosis, kyphosis, and flatback syndrome. We offer advanced surgical and non-surgical treatments to correct alignment and improve function.",
    icon: "🏥",
    color: "#45b7d1",
    treatments: [
      "Spinal Fusion",
      "Growing Rods for Pediatric Patients",
      "Vertebral Body Tethering",
      "Osteotomies",
      "Bracing",
    ],
    conditions: [
      "Adolescent Idiopathic Scoliosis",
      "Adult Degenerative Scoliosis",
      "Scheuermann's Kyphosis",
      "Congenital Deformities",
      "Postural Deformities",
    ],
  },
  "degenerative-clinic": {
    title: "DEGENERATIVE CLINIC",
    description:
      "Disc degeneration is a disease of aging. In certain individuals a degenerated disc can cause severe chronic pain if left untreated.",
    longDescription:
      "Our degenerative clinic focuses on the management of age-related spinal conditions. We provide comprehensive care from early conservative management to advanced surgical interventions when necessary.",
    icon: "⚕️",
    color: "#96ceb4",
    treatments: [
      "Minimally Invasive Decompression",
      "Spinal Fusion",
      "Disc Replacement",
      "Physical Therapy",
      "Regenerative Medicine",
    ],
    conditions: [
      "Degenerative Disc Disease",
      "Spinal Stenosis",
      "Spondylolisthesis",
      "Facet Joint Arthritis",
      "Osteoarthritis",
    ],
  },
  "endoscopy-clinic": {
    title: "ENDOSCOPY AND MINIMAL INVASIVE CLINIC",
    description:
      "Endoscopic laser spine surgery is a minimally invasive method of performing spinal surgery. Recovery is much faster.",
    longDescription:
      "Our minimally invasive clinic utilizes state-of-the-art endoscopic techniques to perform spinal surgery through tiny incisions. This approach results in less tissue damage, reduced pain, faster recovery, and better outcomes.",
    icon: "🔬",
    color: "#ffcc5c",
    treatments: [
      "Endoscopic Discectomy",
      "Laser Spine Surgery",
      "Minimally Invasive Fusion",
      "Percutaneous Screw Fixation",
      "Kyphoplasty",
    ],
    conditions: [
      "Herniated Disc",
      "Spinal Stenosis",
      "Vertebral Compression Fractures",
      "Spinal Tumors",
      "Spinal Infections",
    ],
  },
  "osteoporosis-clinic": {
    title: "OSTEOPOROSIS CLINIC",
    description:
      "Osteoporosis is a condition of fragile bone with an increased susceptibility to fracture. Patients have no symptoms until bone fractures occur.",
    longDescription:
      "Our osteoporosis clinic provides comprehensive evaluation and treatment for patients with low bone density. We focus on prevention, early detection, and management to reduce fracture risk and maintain bone health.",
    icon: "💪",
    color: "#ff6f69",
    treatments: [
      "Bone Density Testing",
      "Medication Management",
      "Nutritional Counseling",
      "Fall Prevention Programs",
      "Kyphoplasty for Fractures",
    ],
    conditions: [
      "Osteopenia",
      "Osteoporosis",
      "Vertebral Compression Fractures",
      "Hip Fractures",
      "Wrist Fractures",
    ],
  },
  "trauma-clinic": {
    title: "TRAUMA & FACTURE CLINIC",
    description:
      "A spinal cord injury (SCI) that can refers to any injury to the spinal cord that is caused by trauma instead of disease.",
    longDescription:
      "Our trauma and rehabilitation clinic provides comprehensive care for patients with acute spinal injuries. We offer emergency surgical intervention, intensive rehabilitation, and long-term follow-up to maximize recovery.",
    icon: "🏃",
    color: "#88b8b0",
    treatments: [
      "Emergency Spinal Surgery",
      "Fracture Fixation",
      "Rehabilitation Programs",
      "Physical Therapy",
      "Occupational Therapy",
    ],
    conditions: [
      "Spinal Fractures",
      "Spinal Cord Injury",
      "Dislocations",
      "Traumatic Disc Herniation",
      "Spinal Instability",
    ],
  },
  "tumour-clinic": {
    title: "TUMOUR CLINIC",
    description:
      "A spinal tumor is a growth of cells (mass) in or surrounding the spinal cord. A small number occur in the nerves of the spinal cord itself.",
    longDescription:
      "Our tumor clinic specializes in the diagnosis and treatment of primary and metastatic spinal tumors. We work closely with oncologists to provide comprehensive care including surgical resection, radiation therapy, and chemotherapy.",
    icon: "🎗️",
    color: "#c06c84",
    treatments: [
      "Tumor Resection",
      "Spinal Reconstruction",
      "Radiation Therapy",
      "Chemotherapy",
      "Pain Management",
    ],
    conditions: [
      "Primary Spinal Tumors",
      "Metastatic Spinal Tumors",
      "Intradural Tumors",
      "Extradural Tumors",
      "Vertebral Hemangiomas",
    ],
  },
  "infection-clinic": {
    title: "INFECTION CLINIC",
    description:
      "The vertebral column, intervertebral discs, dural sac or the space around the spinal cord may become infected by bacteria or fungal organisms.",
    longDescription:
      "Our infection clinic specializes in the management of spinal infections including discitis, osteomyelitis, and epidural abscesses. We provide comprehensive antibiotic therapy and surgical intervention when necessary.",
    icon: "🦠",
    color: "#f08a5d",
    treatments: [
      "IV Antibiotic Therapy",
      "Surgical Debridement",
      "Spinal Reconstruction",
      "Pain Management",
      "Rehabilitation",
    ],
    conditions: [
      "Discitis",
      "Vertebral Osteomyelitis",
      "Epidural Abscess",
      "Post-Operative Infections",
      "Tuberculosis of Spine",
    ],
  },
  // NEWLY ADDED SERVICES:
  "joint-replacement-clinic": {
    title: "JOINT REPLACEMENT CLINIC",
    description:
      "A Joint Replacement Clinic specializes in diagnosing and treating severe joint pain and mobility issues caused by arthritis, injury, or degenerative conditions.",
    longDescription:
      "Our Joint Replacement Clinic offers advanced surgical solutions for hip, knee, and shoulder replacements. We use the latest techniques including minimally invasive approaches and robotic-assisted surgery to ensure optimal outcomes and faster recovery times.",
    icon: "🦿",
    color: "#6c5ce7",
    treatments: [
      "Total Hip Replacement",
      "Total Knee Replacement",
      "Partial Knee Replacement",
      "Shoulder Replacement",
      "Revision Joint Replacement",
    ],
    conditions: [
      "Osteoarthritis",
      "Rheumatoid Arthritis",
      "Avascular Necrosis",
      "Post-Traumatic Arthritis",
      "Congenital Joint Disorders",
    ],
  },
  "arthroscopy-sports-medicine-clinic": {
    title: "ARTHROSCOPY & SPORTS MEDICINE CLINIC",
    description:
      "Our Arthroscopic and Sports Medicine department specializes in minimally invasive joint surgeries and comprehensive treatment of sports-related injuries.",
    longDescription:
      "Our Arthroscopy and Sports Medicine Clinic provides cutting-edge minimally invasive procedures for joint conditions and sports injuries. We focus on getting athletes and active individuals back to their peak performance levels quickly and safely.",
    icon: "⚽",
    color: "#0984e3",
    treatments: [
      "Knee Arthroscopy",
      "Shoulder Arthroscopy",
      "ACL Reconstruction",
      "Meniscus Repair",
      "Rotator Cuff Repair",
    ],
    conditions: [
      "ACL Tears",
      "Meniscus Tears",
      "Rotator Cuff Injuries",
      "Cartilage Damage",
      "Sports-Related Fractures",
    ],
  },
  "physiotherapy-and-rehab-clinic": {
    title: "PHYSIOTHERAPY AND REHAB CLINIC",
    description:
      "Our Physiotherapy and Rehab Clinic focuses on restoring movement, reducing pain, and improving strength through personalized therapy programs.",
    longDescription:
      "Our Physiotherapy and Rehabilitation Clinic offers comprehensive therapeutic programs designed to restore function, reduce pain, and prevent disability. We create personalized treatment plans that address each patient's unique needs and goals.",
    icon: "🏃‍♂️",
    color: "#00b894",
    treatments: [
      "Manual Therapy",
      "Therapeutic Exercise",
      "Electrotherapy",
      "Post-Surgical Rehabilitation",
      "Sports Rehabilitation",
    ],
    conditions: [
      "Post-Surgical Recovery",
      "Sports Injuries",
      "Chronic Pain",
      "Mobility Disorders",
      "Neurological Conditions",
    ],
  },
};

// Special services data
const specialServicesData = {
  "surgical-treatment": {
    title: "Surgical Treatment",
    description:
      "Advanced surgical solutions including joint replacement, fracture fixation, and spine correction to restore function and quality of life.",
    longDescription:
      "Our surgical team specializes in a wide range of orthopedic procedures using the latest techniques and technology. We focus on minimally invasive approaches whenever possible to reduce recovery time and improve outcomes.",
    icon: "🔪",
    img: null,
    procedures: [
      "Joint Replacement (Hip, Knee, Shoulder)",
      "Spinal Fusion",
      "Fracture Fixation",
      "Arthroscopic Surgery",
      "Deformity Correction",
    ],
  },
  "physical-therapy": {
    title: "Physical Therapy",
    description:
      "Personalized rehabilitation programs focused on pain relief, mobility restoration, and long-term recovery after injuries or surgery.",
    longDescription:
      "Our physical therapy department offers comprehensive rehabilitation services tailored to each patient's specific needs. We use evidence-based techniques to help patients regain strength, mobility, and function.",
    icon: "🤸",
    img: null,
    therapies: [
      "Manual Therapy",
      "Therapeutic Exercise",
      "Neuromuscular Re-education",
      "Gait Training",
      "Balance and Coordination Training",
    ],
  },
  "preventive-care": {
    title: "Preventive Care",
    description:
      "Comprehensive bone and joint health programs designed to prevent injuries, manage early symptoms, and promote lifelong wellness.",
    longDescription:
      "Our preventive care programs focus on maintaining optimal musculoskeletal health through education, lifestyle modifications, and early intervention strategies.",
    icon: "🛡️",
    img: null,
    services: [
      "Bone Density Screening",
      "Posture Assessment",
      "Ergonomic Consultation",
      "Nutritional Counseling",
      "Exercise Prescription",
    ],
  },
  diagnostic: {
    title: "Diagnostic",
    description:
      "Accurate diagnosis with the latest imaging and minimally invasive techniques to detect orthopedic conditions early and precisely.",
    longDescription:
      "Our diagnostic center features state-of-the-art imaging technology and minimally invasive diagnostic procedures to accurately identify orthopedic conditions and guide treatment decisions.",
    icon: "🔍",
    img: null,
    diagnostics: [
      "MRI",
      "CT Scan",
      "X-Ray",
      "Ultrasound",
      "Electrodiagnostic Studies",
    ],
  },
  "dg-shipping": {
    title: "DG Shipping",
    description:
      "Global delivery of specialized orthopedic solutions, ensuring patients receive quality treatment options no matter where they are.",
    longDescription:
      "Our DG Shipping approved services ensure that seafarers and maritime personnel receive comprehensive orthopedic care that meets international standards and regulatory requirements.",
    icon: "🚢",
    img: null,
    services: [
      "Medical Examinations",
      "Injury Treatment",
      "Rehabilitation Services",
      "Telemedicine Consultations",
      "Medical Certifications",
    ],
  },
};

const RecommendedService = () => {
  const { serviceId } = useParams();

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth", // This gives a smooth scrolling effect
    });
  }, [serviceId]); // Re-run when serviceId changes

  // Check if it's a main service or special service
  const service = servicesData[serviceId] || specialServicesData[serviceId];

  if (!service) {
    return (
      <div className="container py-5 text-center">
        <h2>Service Not Found</h2>
        <Link to="/" className="btn btn-primary mt-3">
          Return to Home
        </Link>
      </div>
    );
  }

  return (
    <section className="py-5" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="container">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/#services">Services</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {service.title}
            </li>
          </ol>
        </nav>

        {/* Header Section */}
        <motion.div
          className="row align-items-center mb-5"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="col-lg-8">
            <div className="d-flex align-items-center mb-3">
              <span
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: "70px",
                  height: "70px",
                  background: `${service.color}20`,
                  color: service.color,
                  fontSize: "2rem",
                }}
              >
                {service.icon}
              </span>
              <h1 className="display-5 fw-bold mb-0">{service.title}</h1>
            </div>
            <p className="lead text-secondary">
              {service.longDescription || service.description}
            </p>
          </div>
          <div className="col-lg-4 text-lg-end">
            <a
              href="https://indiahealthsystem.com/"
              className="btn btn-primary btn-lg rounded-pill px-5"
            >
              Book Appointment
            </a>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="row g-4">
          {/* Left Column - Main Info */}
          <div className="col-lg-8">
            {/* Overview Card */}
            <motion.div
              className="card border-0 shadow-sm rounded-4 mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="card-body p-4">
                <h3 className="h4 fw-bold mb-4">Overview</h3>
                <p className="text-secondary mb-4">{service.description}</p>

                {/* Treatments/Services List */}
                <div className="row">
                  {(service.treatments ||
                    service.procedures ||
                    service.therapies ||
                    service.services ||
                    service.diagnostics) && (
                    <div className="col-md-6">
                      <h4 className="h5 fw-semibold mb-3">
                        {service.treatments
                          ? "Treatments Offered"
                          : service.procedures
                            ? "Procedures"
                            : service.therapies
                              ? "Therapies"
                              : service.diagnostics
                                ? "Diagnostic Services"
                                : "Services"}
                      </h4>
                      <ul className="list-unstyled">
                        {(
                          service.treatments ||
                          service.procedures ||
                          service.therapies ||
                          service.services ||
                          service.diagnostics ||
                          []
                        ).map((item, index) => (
                          <motion.li
                            key={index}
                            className="mb-2 d-flex align-items-center"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + index * 0.1 }}
                          >
                            <i
                              className="bi bi-check-circle-fill text-success me-2"
                              style={{ fontSize: "1rem" }}
                            ></i>
                            <span>{item}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {service.conditions && (
                    <div className="col-md-6">
                      <h4 className="h5 fw-semibold mb-3">
                        Conditions Treated
                      </h4>
                      <ul className="list-unstyled">
                        {service.conditions.map((condition, index) => (
                          <motion.li
                            key={index}
                            className="mb-2 d-flex align-items-center"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                          >
                            <i
                              className="bi bi-check-circle-fill text-primary me-2"
                              style={{ fontSize: "1rem" }}
                            ></i>
                            <span>{condition}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Why Choose Us Card */}
            <motion.div
              className="card border-0 shadow-sm rounded-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="card-body p-4">
                <h3 className="h4 fw-bold mb-4">
                  Why Choose Our {service.title}
                </h3>
                <div className="row g-4">
                  {[
                    "Expert Specialists with Years of Experience",
                    "State-of-the-Art Technology",
                    "Personalized Treatment Plans",
                    "Comprehensive Care Approach",
                    "Proven Track Record of Success",
                  ].map((point, index) => (
                    <div className="col-md-6" key={index}>
                      <div className="d-flex">
                        <div
                          className="rounded-circle flex-shrink-0 me-3"
                          style={{
                            width: "30px",
                            height: "30px",
                            background: `${service.color}20`,
                            color: service.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1rem",
                          }}
                        >
                          <i className="bi bi-star-fill"></i>
                        </div>
                        <span>{point}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="col-lg-4">
            {/* Contact Card */}
            <motion.div
              className="card border-0 shadow-sm rounded-4 mb-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="card-body p-4">
                <h4 className="h5 fw-bold mb-3">Need Help?</h4>
                <p className="text-secondary small mb-4">
                  Our team is here to assist you with any questions about our{" "}
                  {service.title.toLowerCase()}.
                </p>
                <div className="d-flex align-items-center mb-3">
                  <i className="bi bi-telephone-fill text-primary me-3"></i>
                  <div>
                    <span className="small text-secondary">Call us</span>
                    <div className="fw-semibold">+91 7873366631</div>
                  </div>
                </div>
                <div className="d-flex align-items-center mb-4">
                  <i className="bi bi-envelope-fill text-primary me-3"></i>
                  <div>
                    <span className="small text-secondary">Email</span>
                    <div className="fw-semibold">
                      info.orthospinecare@gmail.com
                    </div>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="btn btn-outline-primary w-100 rounded-pill"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>

            {/* Quick Links Card */}
            <motion.div
              className="card border-0 shadow-sm rounded-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="card-body p-4">
                <h4 className="h5 fw-bold mb-3">Other Services</h4>
                <ul className="list-unstyled">
                  {Object.entries(servicesData)
                    .slice(0, 5)
                    .map(([id, s], index) => (
                      <li key={id} className="mb-2">
                        <Link
                          to={`/service/${id}`}
                          className="text-decoration-none d-flex align-items-center text-dark"
                        >
                          <span
                            className="rounded-circle d-flex align-items-center justify-content-center me-2"
                            style={{
                              width: "30px",
                              height: "30px",
                              background: `${s.color}20`,
                              color: s.color,
                              fontSize: "0.9rem",
                            }}
                          >
                            {s.icon}
                          </span>
                          <span>{s.title}</span>
                          <i className="bi bi-chevron-right ms-auto text-secondary"></i>
                        </Link>
                      </li>
                    ))}
                </ul>
                <Link to="/#services" className="btn btn-link text-primary p-0">
                  View All Services <i className="bi bi-arrow-right ms-1"></i>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecommendedService;
